import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import * as Colors from 'HubStyleTokens/colors';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { getTokenIconFromCategory } from 'data-token-views/utils/tokens';
import { getWebFontFamily } from './utils/tokens';
import { jsx as _jsx } from "react/jsx-runtime";
const UPSCALING_FACTOR = 4;
const align = value => Math.floor(value / UPSCALING_FACTOR) * UPSCALING_FACTOR;
const renderToken = ({
  text,
  category
}) => {
  const tokenHeight = 26 * UPSCALING_FACTOR;
  const textBaselineBuffer = 7 * UPSCALING_FACTOR;
  const tokenWidthBuffer = 16 * UPSCALING_FACTOR;
  const spaceAfterIcon = 8 * UPSCALING_FACTOR;
  const textSize = 16 * UPSCALING_FACTOR;
  const font = `${textSize}px ${getWebFontFamily()}`;
  const iconFont = `${textSize}px spacesword`;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    return null;
  }
  const tokenColors = {
    color: Colors.PANTERA,
    backgroundColor: Colors.OLAF
  };
  const tokenIcon = getTokenIconFromCategory(category);
  context.font = font;
  const textWidth = context.measureText(text).width;
  context.font = iconFont;
  const iconWidth = context.measureText(tokenIcon).width;

  // space breakdown of token image along width
  // | 1/2 token height buffer | icon width | 8px | text width | 1/2 token height buffer |
  const totalWidth = iconWidth + textWidth + tokenWidthBuffer + spaceAfterIcon;
  canvas.setAttribute('width', `${align(totalWidth)}px`);
  canvas.setAttribute('height', `${align(tokenHeight)}px`);
  context.fillStyle = tokenColors.color;
  context.textAlign = 'left';
  context.font = iconFont;
  let x = tokenWidthBuffer / 2;
  let y = tokenHeight - textBaselineBuffer;
  context.fillText(tokenIcon, align(x), align(y));
  context.font = font;
  x = totalWidth - textWidth - tokenWidthBuffer / 2;
  y = tokenHeight - textBaselineBuffer;
  context.fillText(text, align(x), align(y));
  return canvas;
};
const DataToken = ({
  fullToken,
  category,
  label,
  disabled,
  disabledTooltip
}) => {
  const tokenCanvas = useMemo(() => {
    return renderToken({
      text: label,
      category
    });
  }, [label, category]);
  const theme = useTheme();
  if (!tokenCanvas) {
    return /*#__PURE__*/_jsx("span", {
      children: fullToken
    });
  }
  const width = tokenCanvas.getAttribute('width') || '';
  const height = tokenCanvas.getAttribute('height') || '';
  const styles = {
    width: `${Math.floor(parseInt(width, 10) / UPSCALING_FACTOR) + 2}px`,
    height: `${Math.floor(parseInt(height, 10) / UPSCALING_FACTOR) + 2}px`,
    background: disabled ? theme.color['fill-alert-subtle'] : theme.color['fill-surface-default'],
    border: `1px solid ${disabled ? theme.color['border-alert-default'] : theme.color['border-core-default']}`,
    borderRadius: '2px',
    verticalAlign: 'middle',
    boxSizing: 'border-box'
  };
  const tokenImage =
  /*#__PURE__*/
  // @bend-expect trellis-tokenized
  _jsx("img", {
    "data-test-id": fullToken,
    src: tokenCanvas.toDataURL(),
    "data-hubl": fullToken,
    style: styles
  });
  if (disabledTooltip) {
    return /*#__PURE__*/_jsx(UITooltip, {
      title: disabledTooltip,
      placement: "top",
      children: tokenImage
    });
  }
  return tokenImage;
};
DataToken.propTypes = {
  fullToken: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  disabledTooltip: PropTypes.string
};
export default DataToken;